<template>
    <LoadingCard v-if="loading" />
    <div v-else>
        <div class="toolbar">
            <div class="toolbar__label">
                Growsafe certificates for spray applicators
            </div>
            <div class="actions">
                <i class="actions__item zmdi zmdi-search" @click="enableSearch"></i>
                <i class="actions__item zmdi zmdi-plus" v-b-tooltip="'Add a certificate'" @click="enableCreatingCertificate"></i>
            </div>

            <div class="toolbar__search" :class="searchEnabled ? 'd-block' : 'd-none'">
                <input type="text" placeholder="Search..." ref="searchInput" v-model="searchTerm">
                <i class="toolbar__search__close zmdi zmdi-long-arrow-left" @click="disableSearch"></i>
            </div>
        </div>

        <div class="card">
            <div class="card-header bg-primary text-white">
                Growsafe Certificates
            </div>
            <b-table small hover stacked="md" :items="filteredData" :fields="table.fields" sort-by="name" :show-empty="true">
                <template slot="name" slot-scope="row">
                    {{ row.item.first_name }} {{ row.item.last_name }}
                </template>

                <template slot="expires_on" slot-scope="row">
                    <template v-if="row.item.expires_on">
                        {{ row.item.expires_on | date_format }}
                    </template>
                    <span v-else class="text-muted">(unknown)</span>
                </template>
                <template slot="actions" slot-scope="row">

                    <b-button variant="secondary btn--icon"
                              v-b-tooltip.hover="'Edit Certificate'"
                              @click.prevent="enableEditingCertificate(row.item)">
                        <i class="zmdi zmdi-edit"></i>
                    </b-button>

                    <a :href="row.item.certificate" :download="buildFileName(row.item)" class="btn btn-orange btn--icon"
                       v-b-tooltip="'Download Certificate'"
                       v-if="row.item.certificate">
                        <i class="zmdi zmdi-download"></i>
                    </a>
                    <button class="btn btn-orange btn--icon" disabled v-else>
                        <i class="zmdi zmdi-download"></i>
                    </button>
                </template>
            </b-table>
        </div>

        <b-modal id="editModal" size="md" ref="modal" v-model="editingEnabled" :lazy="true"
                 :title="`${editingCertificate ? 'Edit' : 'New'} Growsafe Certificate`"
                 :ok-disabled="buttonsDisabled" :cancel-disabled="buttonsDisabled"
                 @hidden="disableEditingCertificate" @ok="handleCertificateSave">

            <div class="form-group">
                <label>First Name</label>
                <div class="position-relative">
                    <input type="text" name="firstName" data-vv-as="first name" v-validate="{required: true}" class="form-control" v-model="firstName">
                    <div class="invalid-feedback" v-if="errors.has('firstName')">{{ errors.first('firstName') }}</div>
                    <i class="form-group__bar"></i>
                </div>
            </div>

            <div class="form-group">
                <label>Last Name</label>
                <div class="position-relative">
                    <input type="text" name="lastName" data-vv-as="last name" v-validate="{required: true}" class="form-control" v-model="lastName">
                    <div class="invalid-feedback" v-if="errors.has('lastName')">{{ errors.first('lastName') }}</div>
                    <i class="form-group__bar"></i>
                </div>
            </div>

            <div class="form-group">
                <label>Certificate Number</label>
                <div class="position-relative">
                    <input type="text" name="certificateNumber" data-vv-as="certificate number" v-validate="{required: true}" class="form-control" v-model="certificateNumber">
                    <div class="invalid-feedback" v-if="errors.has('certificateNumber')">{{ errors.first('certificateNumber') }}</div>
                    <i class="form-group__bar"></i>
                </div>
            </div>

            <div class="form-group">
                <label>Certificate Expiry</label>
                <div class="position-relative">
                    <VueDatePicker
                            id="date"
                            v-model="expiry"
                            :config="datePicker"
                            class="form-control"
                            :class="{'is-invalid': errors.has('expiry')}"
                            placeholder="Select date"
                            v-validate="{ required: true }"
                            name="expiry" />
                    <i class="form-group__bar"></i>
                    <div class="invalid-feedback" v-if="errors.has('expiry')">{{ errors.first('expiry') }}</div>
                </div>
            </div>

            <div class="form-group">
                <label>Certificate Image
                    <template v-if="editingCertificate"> (leave empty if you do not wish to change)</template>
                </label>
                <div class="position-relative">
                    <b-form-file v-model="certificateImage" @input="uploadNewCertificateImage" accept="image/*" placeholder="Choose an image..." />
                    <b-progress :value="certificateImageProgress" animated v-if="certificateImageProgress"
                                :variant="certificateImageProgressVariant" />
                </div>
            </div>
        </b-modal>
    </div>
</template>
<script>
import createGrowsafeUserMutation from '@views/Setup/GrowsafeManager/mutationCreateGrowsafeUser.graphql';
import updateGrowsafeUserMutation from '@views/Setup/GrowsafeManager/mutationUpdateGrowsafeUser.graphql';
import listGrowsafeUsersQuery from '@views/Setup/GrowsafeManager/queryListGrowsafeUsers.graphql';

const datePickerConfig = (config = {}) => {
  return {
    wrap: true, // set wrap to true only when using 'input-group'
    altInput: true,
    enableTime: false,
    nextArrow: '<i class="zmdi zmdi-long-arrow-right" />',
    prevArrow: '<i class="zmdi zmdi-long-arrow-left" />',
    ...config,
  };
};

/**
 * @property {String} searchTerm
 */
export default {
  name: 'GrowsafeManager',
  apollo: {
    growsafeUsers: {
      query: listGrowsafeUsersQuery,
      update: data => data.filterGrowsafeUsers.data,
    },
  },
  data() {
    return {
      growsafeUsers: [],
      searchEnabled: false,
      editingEnabled: false,
      editingCertificate: null,
      firstName: null,
      lastName: null,
      certificateNumber: null,
      certificateImage: null,
      certificateImageProgress: 0,
      certificateImageProgressVariant: 'primary',
      certificateImageId: null,
      buttonsDisabled: false,
      expiry: null,
      searchTerm: '',
      loading: 0,
      datePicker: datePickerConfig({
        altFormat: 'M j, Y',
        dateFormat: 'Y-m-d',
      }),
    };
  },
  computed: {
    table() {
      return {
        fields: [
          { key: 'name', label: 'Name' },
          { key: 'certificate_number', label: 'Certificate' },
          { key: 'expires_on', label: 'Expiry' },
          { key: 'actions', label: '', class: 'text-right' },
        ],
      };
    },
    filteredData() {
      const fullName = (item) => `${item.first_name} ${item.last_name}`;
      const subjectMatchesSearch = subject => {
        return (
          this.searchTerm.length === 0 ||
          fullName(subject).toLowerCase().includes(this.searchTerm.toLowerCase()) ||
          subject.certificate_number.toLowerCase().includes(this.searchTerm.toLowerCase())
        );
      };

      return this.growsafeUsers.filter(subjectMatchesSearch).sort((a, b) => fullName(a).localeCompare(fullName(b)));
    },
  },
  mounted() {
  },
  methods: {
    buildFileName(item) {
      if (item.certificate) {
        const extension = item.certificate.substr(item.certificate.lastIndexOf('.'));
        return `${item.certificate_number}${extension}`;
      }
    },
    uploadNewCertificateImage(file) {
      this.buttonsDisabled = true;
      this.certificateImageId = null;
      if (file) {
        this.$uploadSingleFile(file, {}, progress => {
            this.certificateImageProgress = Math.round(progress.loaded * 100 / progress.total);
          })
          .then(({ data: { file_id } }) => {
            this.buttonsDisabled = false;
            this.certificateImageProgressVariant = 'success';
            this.certificateImageId = file_id;
            console.log(file_id);
          })
          .catch(err => {
            this.buttonsDisabled = false;
            this.certificateImageProgressVariant = 'danger';
            console.error('failed', err);
            this.$notifyFailure('Unfortunately, we were unable to do that!');
          });
      }
    },
    handleCertificateSave(evt) {
      evt.preventDefault();
      this.$validator.validateAll().then(validationWasSuccessful => {
        if (validationWasSuccessful && this.editingCertificate) {
          this.updateCertificate();
        } else if (validationWasSuccessful) {
          this.createCertificate();
        }
      });
    },

    updateCertificate() {
      const input = {
        id: this.editingCertificate.id,
        first_name: this.firstName,
        last_name: this.lastName,
        certificate_number: this.certificateNumber,
        expires_on: this.expiry,
      };

      if (this.certificateImageId) {
        input.certificate_id = this.certificateImageId;
      }

      this.$apollo.mutate({
          mutation: updateGrowsafeUserMutation,
          variables: { input },
        })
        .then(() => {
          this.$notifySuccess('Growsafe Certificate updated!');
          this.disableEditingCertificate();
        })
        .catch(err => {
          this.$mapGraphQLError(err);
        })
        .catch(err => {
          console.error(err);
          this.$notifyFailure('Unfortunately, we were unable to do that!');
        });
    },
    createCertificate() {
      const input = {
        first_name: this.firstName,
        last_name: this.lastName,
        certificate_number: this.certificateNumber,
        expires_on: this.expiry,
      };

      if (this.certificateImageId) {
        input.certificate_id = this.certificateImageId;
      }

      this.$apollo.mutate({
          mutation: createGrowsafeUserMutation,
          variables: { input },
        })
        .then(() => {
          this.$notifySuccess('Growsafe Certificate created!');
          this.$apollo.queries.growsafeUsers.refetch();
          this.disableEditingCertificate();
        })
        .catch(err => {
          this.$mapGraphQLError(err);
        })
        .catch(err => {
          console.error(err);
          this.$notifyFailure('Unfortunately, we were unable to do that!');
        });
    },
    enableCreatingCertificate() {
      this.editingEnabled = true;
    },
    enableEditingCertificate(certificate) {
      this.editingCertificate = certificate;
      this.firstName = certificate.first_name;
      this.lastName = certificate.last_name;
      this.certificateNumber = certificate.certificate_number;
      this.expiry = certificate.expires_on;
      this.editingEnabled = true;
    },
    disableEditingCertificate() {
      this.editingCertificate = null;
      this.firstName = null;
      this.lastName = null;
      this.certificateNumber = null;
      this.certificateImage = null;
      this.certificateImageId = null;
      this.certificateImageProgress = 0;
      this.certificateImageProgressVariant = 'primary';
      this.expiry = null;
      this.editingEnabled = false;
    },
    enableSearch() {
      this.searchEnabled = true;
      this.$nextTick(() => this.$refs.searchInput.focus());
    },
    disableSearch() {
      this.searchTerm = '';
      this.searchEnabled = false;
    },
  },
};
</script>
